/**
 * Responsibility: Implement commerce runtime behavior for entitlement resolution, usage facts, and purchase receipts.
 * Scope: Runtime persistence and remote sync only; UI orchestration stays in stores/screens.
 */
import { getProxyAuthorizationHeader } from '../ai/proxy-auth'
import {
  loadAdminConfig,
  listMarketplaceReceipts as backendListMarketplaceReceipts,
  listManagedUsage as backendListManagedUsage,
  readBillingAuditSnapshot as backendReadBillingAuditSnapshot,
  writeManagedUsage as backendWriteManagedUsage,
  writeMarketplaceReceipt as backendWriteMarketplaceReceipt,
} from "@anyquest/shared-core"
import { authStore } from '../ui/stores/auth-store'
import type { MarketplaceWorldManifest } from '../engine/marketplace'
import {
  type CommercePlan,
  type CommerceTesterGrant,
  type CommerceUsageMode,
  type EntitlementSnapshot,
  type ManagedUsageEntry,
  type ManagedUsageKind,
  type ManagedUsageSummary,
  type MarketplaceArtifactReceipt,
  type PreforgedOffer,
  DEFAULT_COMMERCE_PLANS,
  buildEntitlementSnapshot,
  describeMarketplaceAccess,
  grantMatchesIdentity,
  normalizeCommerceTesterGrants,
  normalizeManagedUsageLedger,
  normalizeMarketplaceArtifactReceipt,
  normalizeMarketplaceArtifactReceipts,
  normalizeEntitlementSnapshot,
  normalizePreforgedOffers,
  planById,
  readLocalCommerceTesterGrants,
  readLocalCommercePlans,
  readLocalEntitlementSnapshot,
  readLocalManagedUsageLedger,
  readLocalMarketplaceArtifactReceipts,
  readLocalPreforgedOffers,
  saveLocalEntitlementSnapshot,
  saveLocalManagedUsageLedger,
  saveLocalMarketplaceArtifactReceipts,
  summarizeManagedUsage,
} from './commerce-contract'

export interface CommerceAuditEvent {
  id: string
  type: string
  uid: string
  status: string
  plan: string
  claimsChanged: boolean
  processedAtMs: number
  customerId: string
  subscriptionId: string
}

export interface CommerceBillingAuditSnapshot {
  source: 'backend' | 'local'
  processedCount: number
  ignoredCount: number
  planDriftCount: number
  customerMapCount: number
  events: CommerceAuditEvent[]
}

function currentUserIdentity(): { uid: string; email: string } | null {
  const user = authStore.user
  if (user?.uid) {
    return {
      uid: String(user.uid).trim(),
      email: String(user.email || '').trim().toLowerCase(),
    }
  }
  return null
}

function proxyBase(): string {
  const metaEnv = ((import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }).env) ?? {}
  const envProxy = String(metaEnv.VITE_GEMINI_PROXY_URL ?? '').trim()
  if (envProxy) return envProxy.replace(/\/+$/, '')
  try {
    const raw = localStorage.getItem('am2_ai_config')
    if (!raw) return ''
    const parsed = JSON.parse(raw)
    return String(parsed?.geminiProxyUrl || '').trim().replace(/\/+$/, '')
  } catch {
    return ''
  }
}

function usageId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

async function readRemoteTesterGrants(): Promise<CommerceTesterGrant[]> {
  try {
    const config = await loadAdminConfig(['testerAccess'])
    return normalizeCommerceTesterGrants((config.testerAccess as { grants?: CommerceTesterGrant[] } | undefined)?.grants)
  } catch {
    return []
  }
}

function listMatchingTesterGrants(
  grants: CommerceTesterGrant[],
  identity: { uid: string; email: string } | null,
): CommerceTesterGrant[] {
  if (!identity) return []
  return grants.filter(grant => grantMatchesIdentity(grant, identity))
}

function elevateEntitlementFromGrants(
  entitlement: EntitlementSnapshot,
  grants: CommerceTesterGrant[],
  plans: CommercePlan[],
): EntitlementSnapshot {
  const activePlanGrants = grants.filter(grant => grant.kind === 'plan' && grant.state === 'active' && grant.planId)
  if (!activePlanGrants.length) return entitlement
  const grantedPlanId = activePlanGrants.reduce((best, grant) => (
    grant.planId && planById(plans, grant.planId).monthlyPriceUsdCents >= planById(plans, best).monthlyPriceUsdCents
      ? grant.planId
      : best
  ), entitlement.planId)
  const grantedPlan = planById(plans, grantedPlanId)
  const currentPlan = planById(plans, entitlement.planId)
  const useGrantedPlan = grantedPlan.monthlyPriceUsdCents >= currentPlan.monthlyPriceUsdCents
  if (!useGrantedPlan && entitlement.active) return entitlement
  return normalizeEntitlementSnapshot({
    ...entitlement,
    planId: useGrantedPlan ? grantedPlan.id : currentPlan.id,
    active: true,
    hostedProxyAccess: useGrantedPlan ? grantedPlan.hostedProxyAccess : entitlement.hostedProxyAccess,
    managedImages: useGrantedPlan ? grantedPlan.managedImages : entitlement.managedImages,
    creatorConsoleAccess: useGrantedPlan ? grantedPlan.creatorConsoleAccess : entitlement.creatorConsoleAccess,
    preforgedRealityAccess: useGrantedPlan ? grantedPlan.preforgedRealityAccess : entitlement.preforgedRealityAccess,
    managedActionQuota: useGrantedPlan ? grantedPlan.managedActionQuota : entitlement.managedActionQuota,
    managedImageQuota: useGrantedPlan ? grantedPlan.managedImageQuota : entitlement.managedImageQuota,
    creatorPublishSlots: useGrantedPlan ? grantedPlan.creatorPublishSlots : entitlement.creatorPublishSlots,
    source: 'admin',
  }, plans)
}

function receiptFromTesterGrant(
  grant: CommerceTesterGrant,
  identity: { uid: string; email: string },
  entitlement: EntitlementSnapshot,
): MarketplaceArtifactReceipt | null {
  if (grant.kind !== 'marketplace_world' || grant.state !== 'active' || !grant.artifactId) return null
  return normalizeMarketplaceArtifactReceipt({
    artifactId: grant.artifactId,
    artifactKind: 'marketplace_world',
    userUid: identity.uid,
    userEmail: identity.email,
    title: grant.title || grant.artifactId,
    state: 'owned',
    source: 'admin_grant',
    planId: entitlement.planId,
    amountPaidCents: 0,
    currency: 'USD',
    acquiredAtMs: grant.updatedAtMs || Date.now(),
    updatedAtMs: grant.updatedAtMs || Date.now(),
  })
}

export async function resolveCurrentEntitlement(plans: CommercePlan[] = readLocalCommercePlans()): Promise<EntitlementSnapshot> {
  const fallbackPlans = plans.length ? plans : DEFAULT_COMMERCE_PLANS
  const localSnapshot = readLocalEntitlementSnapshot(fallbackPlans)
  const identity = currentUserIdentity()
  if (!identity) {
    const signedOutSnapshot = normalizeEntitlementSnapshot({
      ...localSnapshot,
      planId: 'free',
      active: false,
      hostedProxyAccess: false,
      managedImages: false,
      creatorConsoleAccess: false,
      preforgedRealityAccess: false,
      managedActionQuota: 0,
      managedImageQuota: 0,
      creatorPublishSlots: 0,
      source: 'default',
    }, fallbackPlans)
    saveLocalEntitlementSnapshot(signedOutSnapshot, fallbackPlans)
    return signedOutSnapshot
  }

  let snapshot = localSnapshot.source === 'admin'
    ? normalizeEntitlementSnapshot({ ...localSnapshot, source: 'default' }, fallbackPlans)
    : localSnapshot

  const base = proxyBase()
  if (base) {
    try {
      const authorization = await getProxyAuthorizationHeader()
      if (authorization) {
        const resp = await fetch(`${base}/entitlement`, {
          method: 'GET',
          headers: { Authorization: authorization },
        })
        if (resp.ok) {
          const json = await resp.json()
          snapshot = normalizeEntitlementSnapshot(json?.entitlement ?? snapshot, fallbackPlans)
        }
      }
    } catch {
      // Local fallback is acceptable.
    }
  }

  const grants = listMatchingTesterGrants(
    normalizeCommerceTesterGrants([
      ...await readRemoteTesterGrants(),
      ...readLocalCommerceTesterGrants(),
    ]),
    identity,
  )
  snapshot = elevateEntitlementFromGrants(snapshot, grants, fallbackPlans)
  saveLocalEntitlementSnapshot(snapshot, fallbackPlans)
  return snapshot
}

export async function recordManagedUsageFact(data: {
  kind: ManagedUsageKind
  usageMode: CommerceUsageMode
  provider: string
  jobType: string
  inputTokens?: number
  outputTokens?: number
}): Promise<ManagedUsageEntry | null> {
  const identity = currentUserIdentity()
  if (!identity) return null
  const entry: ManagedUsageEntry = {
    id: usageId(),
    ts: Date.now(),
    userUid: identity.uid,
    userEmail: identity.email,
    kind: data.kind,
    usageMode: data.usageMode,
    provider: String(data.provider || 'unknown').trim() || 'unknown',
    jobType: String(data.jobType || (data.kind === 'image' ? 'image' : 'logic_forge')).trim(),
    inputTokens: Math.max(0, Math.round(Number(data.inputTokens || 0))),
    outputTokens: Math.max(0, Math.round(Number(data.outputTokens || 0))),
  }
  const ledger = normalizeManagedUsageLedger([entry, ...readLocalManagedUsageLedger()]).slice(0, 1000)
  saveLocalManagedUsageLedger(ledger)

  if (entry.usageMode === 'managed') {
    try {
      await backendWriteManagedUsage(entry)
    } catch {
      // Local ledger remains authoritative fallback.
    }
  }

  return entry
}

export async function listAllManagedUsage(): Promise<ManagedUsageEntry[]> {
  try {
    return normalizeManagedUsageLedger(await backendListManagedUsage())
  } catch {
    return []
  }
}

export async function listCurrentManagedUsage(): Promise<ManagedUsageEntry[]> {
  const identity = currentUserIdentity()
  if (!identity) return []
  return readLocalManagedUsageLedger().filter(entry => entry.userUid === identity.uid)
}

export async function summarizeCurrentManagedUsage(
  entitlement?: EntitlementSnapshot,
  plans: CommercePlan[] = readLocalCommercePlans(),
): Promise<ManagedUsageSummary> {
  const resolvedEntitlement = entitlement ?? await resolveCurrentEntitlement(plans)
  const entries = await listCurrentManagedUsage()
  return summarizeManagedUsage(entries, resolvedEntitlement)
}

async function readRemoteMarketplaceReceipts(uid: string): Promise<MarketplaceArtifactReceipt[]> {
  try {
    return normalizeMarketplaceArtifactReceipts(await backendListMarketplaceReceipts(uid))
  } catch {
    return []
  }
}

export async function listCurrentMarketplaceReceipts(): Promise<MarketplaceArtifactReceipt[]> {
  const identity = currentUserIdentity()
  if (!identity) return []
  const remote = await readRemoteMarketplaceReceipts(identity.uid)
  const local = readLocalMarketplaceArtifactReceipts().filter(receipt => receipt.userUid === identity.uid)
  const grants = await listCurrentTesterGrants()
  const entitlement = readLocalEntitlementSnapshot()
  const granted = grants
    .map(grant => receiptFromTesterGrant(grant, identity, entitlement))
    .filter((receipt): receipt is MarketplaceArtifactReceipt => !!receipt)
  return normalizeMarketplaceArtifactReceipts([...remote, ...local, ...granted])
}

async function persistMarketplaceReceipt(receipt: MarketplaceArtifactReceipt): Promise<void> {
  const localMerged = normalizeMarketplaceArtifactReceipts([
    receipt,
    ...readLocalMarketplaceArtifactReceipts().filter(entry =>
      !(entry.userUid === receipt.userUid && entry.artifactId === receipt.artifactId),
    ),
  ])
  saveLocalMarketplaceArtifactReceipts(localMerged)
  try {
    await backendWriteMarketplaceReceipt(receipt)
  } catch {
    // Local receipt is the reliable fallback.
  }
}

export async function acquireMarketplaceWorldReceipt(
  entry: MarketplaceWorldManifest,
  entitlement: EntitlementSnapshot,
): Promise<MarketplaceArtifactReceipt> {
  const identity = currentUserIdentity()
  if (!identity) throw new Error('Sign in before acquiring marketplace worlds.')
  const access = describeMarketplaceAccess(entry, entitlement, await listCurrentMarketplaceReceipts())
  const priceCents = Number(entry?.pricing?.priceCents || 0)
  const source = access.included
    ? 'included_plan'
    : entry?.pricing?.model === 'free'
      ? 'free'
      : 'purchase'
  const receipt = normalizeMarketplaceArtifactReceipt({
    artifactId: entry.id,
    artifactKind: 'marketplace_world',
    userUid: identity.uid,
    userEmail: identity.email,
    title: entry.title,
    state: 'owned',
    source,
    planId: entitlement.planId,
    amountPaidCents: source === 'purchase' ? priceCents : 0,
    currency: String(entry?.pricing?.currency || 'USD').toUpperCase(),
    acquiredAtMs: Date.now(),
    updatedAtMs: Date.now(),
  })
  if (!receipt) throw new Error('Could not normalize marketplace receipt.')
  await persistMarketplaceReceipt(receipt)
  return receipt
}

export async function describeMarketplaceWorldAccess(
  entry: MarketplaceWorldManifest,
  entitlement?: EntitlementSnapshot,
): Promise<ReturnType<typeof describeMarketplaceAccess>> {
  const resolvedEntitlement = entitlement ?? await resolveCurrentEntitlement()
  const receipts = await listCurrentMarketplaceReceipts()
  return describeMarketplaceAccess(entry, resolvedEntitlement, receipts)
}

export async function listCurrentTesterGrants(): Promise<CommerceTesterGrant[]> {
  const identity = currentUserIdentity()
  if (!identity) return []
  const grants = normalizeCommerceTesterGrants([
    ...await readRemoteTesterGrants(),
    ...readLocalCommerceTesterGrants(),
  ])
  return listMatchingTesterGrants(grants, identity)
}

export async function listPublishedPreforgedOffers(): Promise<PreforgedOffer[]> {
  const local = readLocalPreforgedOffers()
  try {
    const config = await loadAdminConfig(['preforgedCatalog'])
    const remote = normalizePreforgedOffers((config.preforgedCatalog as { offers?: PreforgedOffer[] } | undefined)?.offers)
    const merged = normalizePreforgedOffers([...remote, ...local])
    return merged.filter(offer => offer.publishState === 'published')
  } catch {
    return local.filter(offer => offer.publishState === 'published')
  }
}

function auditEventFromRaw(id: string, raw: Record<string, unknown>): CommerceAuditEvent {
  return {
    id,
    type: String(raw.type || '').trim(),
    uid: String(raw.uid || '').trim(),
    status: String(raw.status || '').trim() || 'unknown',
    plan: String(raw.plan || '').trim(),
    claimsChanged: raw.claimsChanged === true,
    processedAtMs: raw.processed_at ? new Date(raw.processed_at as string).getTime() : 0,
    customerId: String(raw.customerId || '').trim(),
    subscriptionId: String(raw.subscriptionId || '').trim(),
  }
}

export async function readBillingAuditSnapshot(plans: CommercePlan[] = readLocalCommercePlans()): Promise<CommerceBillingAuditSnapshot> {
  const normalizedPlans = plans.length ? plans : DEFAULT_COMMERCE_PLANS
  try {
    const snapshot = await backendReadBillingAuditSnapshot()
    const events = (snapshot.events || []).map(row => auditEventFromRaw(String((row as Record<string, unknown>).id ?? ''), row as Record<string, unknown>))
    const processedCount = events.filter(event => event.status === 'processed').length
    const ignoredCount = events.filter(event => event.status !== 'processed').length
    const planIds = new Set(normalizedPlans.map(plan => plan.id))
    const planDriftCount = events.filter(event => event.plan && !planIds.has(event.plan as never)).length
    
    return {
      source: 'backend',
      processedCount,
      ignoredCount,
      planDriftCount,
      customerMapCount: snapshot.customerCount || 0,
      events,
    }
  } catch {
    return {
      source: 'local',
      processedCount: 0,
      ignoredCount: 0,
      planDriftCount: 0,
      customerMapCount: 0,
      events: [],
    }
  }
}
